import React, { useCallback, useEffect, useState } from "react";
import { IClusterCardProps } from "./ClusterCardInterfaces";
import { Card, CardContent, CircularProgress, IconButton, Typography } from "@mui/material";
import { useStyles } from "./ClusterCard.styles";
import { useTranslation } from "react-i18next";
import useRolesPermissions from "hooks/useRolesPermissions";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckUserPasswordModal from "components/CheckUserPasswordModal";
import { ClusterEntity } from "entities/ClusterEntity/ClusterEntity";

const EMPTY_CLUSTER_CODE = "$$EMPTY_CLUSTER_CODE$$";

export const ClusterCard = ({ cluster, handleOpen, setEditableCluster }: IClusterCardProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { CORPORATE_PERMISSIONS } = useRolesPermissions();
    const [clusterCodeLoading, setClusterCodeLoading] = useState({
        loading: false,
        currentClusterCode: "",
    });
    const [clusterCodeToRefresh, setClusterCodeToRefresh] = useState<string | null>();
    const { refreshClusterCode } = ClusterEntity.UpdateClusterCode();
    const [showChangeClusterCodeWarningModal, setShowChangeClusterCodeWarningModal] = useState(false);
    const [newClusterCode, setNewClusterCode] = useState<string | null>();
    const { cluster: cluster_code } = ClusterEntity.SubscribeClusterCode(cluster?.cluster_id);

    const handleRefresClusterCode = useCallback(
        async (currentCode: string) => {
            setClusterCodeLoading({
                currentClusterCode: currentCode,
                loading: true,
            });
            try {
                await refreshClusterCode({
                    variables: { cluster_code_id: cluster.cluster_id, changes: { cluster_code_refresh: true } },
                });
                setShowChangeClusterCodeWarningModal(true);
            } catch {
                setClusterCodeLoading({
                    currentClusterCode: "",
                    loading: false,
                });
                return;
            }
        },
        [refreshClusterCode, cluster.cluster_id],
    );

    useEffect(() => {
        if (cluster_code[0]?.cluster_code !== clusterCodeLoading.currentClusterCode) {
            setClusterCodeLoading({
                currentClusterCode: "",
                loading: false,
            });
        }
    }, [cluster_code, clusterCodeLoading.currentClusterCode]);

    useEffect(() => {
        if (
            cluster_code[0]?.cluster_code !== clusterCodeLoading.currentClusterCode &&
            !newClusterCode &&
            !clusterCodeLoading.loading &&
            !!showChangeClusterCodeWarningModal
        ) {
            setNewClusterCode(cluster?.cluster_code);
        }
    }, [
        cluster?.cluster_code,
        cluster_code,
        clusterCodeLoading.currentClusterCode,
        clusterCodeLoading.loading,
        newClusterCode,
        showChangeClusterCodeWarningModal,
    ]);

    const handleEditModal = useCallback(() => {
        handleOpen();
        setEditableCluster(cluster);
    }, [cluster, handleOpen, setEditableCluster]);

    return (
        <Card className={classes.cardContainer}>
            <CardContent>
                <div className={`${classes.nameContainer} ${classes.pointer}`} onClick={handleEditModal}>
                    <Typography className={classes.name} gutterBottom variant="subtitle2">
                        {`${t("corporate_backoffice.cluster_view.cluster_title")} ${cluster.name}`}
                    </Typography>
                </div>
                <div className={`${classes.machineCenterNamesList} ${classes.pointer}`} onClick={handleEditModal}>
                    {cluster.machine_centers.length <= 4 &&
                        cluster.machine_centers.map((machineCenter, key) => (
                            <Typography variant="error" key={machineCenter.machine_center_id}>
                                {machineCenter.name}
                            </Typography>
                        ))}
                    {cluster.machine_centers.length > 4 && (
                        <Typography variant="error" className={classes.totalHMIs}>
                            {`${t("corporate_backoffice.cluster_view.total_HMIs")} `}{" "}
                            <span className={classes.totalHMIsAmount}>{cluster.machine_centers.length}</span>
                        </Typography>
                    )}
                </div>
                {CORPORATE_PERMISSIONS?.corporationPermissions?.showClusterCode && (
                    <div className={classes.clusterCodeContainer}>
                        <Typography variant="error" className={classes.clusterCodeText}>{`${t(
                            "corporate_backoffice.cluster_view.cluster_code",
                        )}: ${
                            cluster_code[0]?.cluster_code
                                ? cluster_code[0]?.cluster_code
                                : t("corporate_backoffice.cluster_view.no_cluster_code")
                        }`}</Typography>
                        {CORPORATE_PERMISSIONS?.corporationPermissions?.canEditMachineCenters &&
                            CORPORATE_PERMISSIONS?.corporationPermissions?.canRefreshClusterCode && (
                                <IconButton
                                    className={classes.refreshButton}
                                    disabled={clusterCodeLoading.loading}
                                    onClick={() => setClusterCodeToRefresh(cluster?.cluster_code || EMPTY_CLUSTER_CODE)}
                                >
                                    {clusterCodeLoading.loading ? (
                                        <CircularProgress className={classes.loadingContainer} color="info" />
                                    ) : (
                                        <RefreshIcon className={classes.refreshIcon} />
                                    )}
                                </IconButton>
                            )}
                        <CheckUserPasswordModal
                            open={!!clusterCodeToRefresh}
                            onConfirmPassword={() => handleRefresClusterCode(cluster?.cluster_code || "")}
                            onClose={() => setClusterCodeToRefresh(null)}
                            title={t("corporate_backoffice.cluster_view.confirm_refresh_cluster_code_modal_title")}
                            question={t(
                                "corporate_backoffice.cluster_view.confirm_refresh_cluster_code_modal_question",
                            )}
                            questionConfirmedButtonText={t(
                                "corporate_backoffice.cluster_view.confirm_refresh_cluster_code_modal_title",
                            )}
                        />
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
